import { useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';

const initialFilters = {
    category: 'all',
    concern: 'all',
    priceRange: [0, 500],
    sortBy: 'newest',
    searchQuery: ''
};

const categories = [
    { id: 'all', label: 'All Products', color: 'var(--champagne-gold)' },
    { id: 'skin', label: 'Skincare', color: 'var(--muted-sage)' },
    { id: 'hair', label: 'Hair Care', color: 'var(--champagne-gold)' },
    { id: 'bath', label: 'Bath & Body', color: 'var(--deep-charcoal)' },
    { id: 'sets', label: 'Gift Sets', color: 'var(--muted-sage)' }
];

const concerns = [
    { id: 'all', label: 'All Concerns' },
    { id: 'glow', label: 'Radiance & Glow' },
    { id: 'acne', label: 'Acne & Blemishes' },
    { id: 'hydration', label: 'Deep Hydration' },
    { id: 'aging', label: 'Anti-Aging' },
    { id: 'sensitivity', label: 'Sensitive Skin' },
    { id: 'brightening', label: 'Brightening' }
];

const sortOptions = [
    { id: 'newest', label: 'New Arrivals' },
    { id: 'bestseller', label: 'Best Sellers' },
    { id: 'price-low', label: 'Price: Low to High' },
    { id: 'price-high', label: 'Price: High to Low' },
    { id: 'name', label: 'Name: A to Z' }
];

const priceRanges = [
    { label: 'All Prices', min: 0, max: 500 },
    { label: 'Under $50', min: 0, max: 50 },
    { label: '$50 - $100', min: 50, max: 100 },
    { label: '$100 - $200', min: 100, max: 200 },
    { label: 'Over $200', min: 200, max: 500 }
];

export const useShopFilters = (initialProducts = []) => {
    const [filters, setFilters] = useState(initialFilters);
    const [filteredProducts, setFilteredProducts] = useState(initialProducts);
    const [isFiltering, setIsFiltering] = useState(false);
    const [activeFilterCount, setActiveFilterCount] = useState(0);

    // Count active filters
    useEffect(() => {
        let count = 0;
        if (filters.category !== 'all') count++;
        if (filters.concern !== 'all') count++;
        if (filters.sortBy !== 'newest') count++;
        if (filters.searchQuery.trim() !== '') count++;
        if (filters.priceRange[0] > 0 || filters.priceRange[1] < 500) count++;

        setActiveFilterCount(count);
    }, [filters]);

    // Apply filters function
    const applyFilters = useCallback((products) => {
        if (!products || products.length === 0) return [];

        let filtered = [...products];

        // Apply category filter
        if (filters.category !== 'all') {
            filtered = filtered.filter(product =>
                product.category === filters.category
            );
        }

        // Apply concern filter
        if (filters.concern !== 'all') {
            filtered = filtered.filter(product =>
                product.concerns?.includes(filters.concern)
            );
        }

        // Apply price range filter
        filtered = filtered.filter(product =>
            product.price >= filters.priceRange[0] &&
            product.price <= filters.priceRange[1]
        );

        // Apply search query
        if (filters.searchQuery.trim() !== '') {
            const query = filters.searchQuery.toLowerCase();
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(query) ||
                product.description.toLowerCase().includes(query) ||
                product.benefit.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        switch (filters.sortBy) {
            case 'newest':
                filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
            case 'bestseller':
                filtered.sort((a, b) => b.salesCount - a.salesCount);
                break;
            case 'price-low':
                filtered.sort((a, b) => a.price - b.price);
                break;
            case 'price-high':
                filtered.sort((a, b) => b.price - a.price);
                break;
            case 'name':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            default:
                break;
        }

        return filtered;
    }, [filters]);

    // Debounced filter application
    const debouncedFilter = useCallback(
        debounce((products) => {
            setIsFiltering(true);
            setTimeout(() => {
                const result = applyFilters(products);
                setFilteredProducts(result);
                setIsFiltering(false);
            }, 300); // Simulate API delay
        }, 500),
        [applyFilters]
    );

    // Update filtered products when filters change
    useEffect(() => {
        if (initialProducts.length > 0) {
            debouncedFilter(initialProducts);
        }
    }, [filters, initialProducts, debouncedFilter]);

    // Update single filter
    const updateFilter = useCallback((filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    }, []);

    // Update price range
    const updatePriceRange = useCallback((range) => {
        setFilters(prev => ({
            ...prev,
            priceRange: range
        }));
    }, []);

    // Reset filters
    const resetFilters = useCallback(() => {
        setFilters(initialFilters);
    }, []);

    // Clear specific filter
    const clearFilter = useCallback((filterName) => {
        if (filterName === 'category') {
            updateFilter('category', 'all');
        } else if (filterName === 'concern') {
            updateFilter('concern', 'all');
        } else if (filterName === 'searchQuery') {
            updateFilter('searchQuery', '');
        } else if (filterName === 'priceRange') {
            updatePriceRange([0, 500]);
        } else if (filterName === 'sortBy') {
            updateFilter('sortBy', 'newest');
        }
    }, [updateFilter, updatePriceRange]);

    return {
        filters,
        filteredProducts,
        isFiltering,
        activeFilterCount,
        categories,
        concerns,
        sortOptions,
        priceRanges,
        updateFilter,
        updatePriceRange,
        resetFilters,
        clearFilter,
        setFilters
    };
};